<template>
  <div>
    <b-modal
      id="groupDeleteFromModal"
      :title="title"
      size="lg"
      :no-close-on-backdrop="true"
      ok-title="Eyða"
      :ok-disabled="attendanceSummary.loading || attendanceBlockers.length > 0"
      cancel-title="Hætta við"
      @ok.prevent="validateBeforeSubmit"
      ref="deleteFromGroupModal"
    >
      <div>
        <b-alert variant="danger" :show="attendanceBlockers.length > 0">
          <strong>Ekki er hægt að eyða: viðveruskráning er til staðar</strong>
          <div class="small mt-1">Eyða þarf viðveruskráningu áður en hægt er að eyða skráningunni.</div>
        </b-alert>

        <b-form @submit.prevent="validateBeforeSubmit" novalidate>
          <h5>Ertu viss um að þú viljir eyða eftirfarandi skráningu?</h5>
          <table class="table" v-if="groupPerson && groupPerson.placement_group">
            <tr>
              <td>Tímabil</td>
              <td>{{ groupPerson.placement_group.period.name }}</td>
            </tr>
            <tr v-if="groupPerson.placement_group.period_part">
              <td>Starfstímabil</td>
              <td>
                {{ groupPerson.placement_group.period_part.date_from | moment('DD.MM.YYYY') }} -
                {{ groupPerson.placement_group.period_part.date_to | moment('DD.MM.YYYY') }}
              </td>
            </tr>
            <tr>
              <td>Vinnustaður</td>
              <td>{{ groupPerson.placement_group.workplace.name }}</td>
            </tr>
            <tr>
              <td>Hópur</td>
              <td>{{ groupPerson.placement_group.name }}</td>
            </tr>
            <tr>
              <td>Samtals viðvera</td>
              <td v-if="attendanceSummary.loading || !attendanceSummary.data"><loading /></td>
              <td v-else>{{ attendanceSummary.data.hours | formatMaxHours }}</td>
            </tr>
          </table>

          <b-form-group label-for="full" v-if="attendanceBlockers.length === 0">
            <b-form-checkbox id="full" name="full" v-model="form.full" data-vv-as="eyða">Eyða öllum viðveruskráningum.</b-form-checkbox>
          </b-form-group>

          <b-form-group
            label="Breyting tekur gildi frá og með*"
            label-for="date"
            :state="submitted && errors.has('date') ? false : ''"
            :invalid-feedback="errors.first('date')"
            v-if="!form.full && attendanceBlockers.length === 0"
          >
            <datepicker
              id="date"
              name="date"
              placeholder="Veldu dagsetningu"
              :class="{ 'is-invalid': submitted && errors.has('date') }"
              v-model="form.date"
              :language="lang"
              :monday-first="true"
              format="dd.MM.yyyy"
              :typeable="false"
              data-vv-as="dags"
              :v-validate="form.full ? '' : 'required'"
              ref="dateFromPickr"
              :disabled-dates="disabledDates"
            />
          </b-form-group>
        </b-form>
      </div>
    </b-modal>
  </div>
</template>

<script>
import moment from 'moment';
import { mapActions } from 'vuex';
import { is } from 'vuejs-datepicker/dist/locale';
import Datepicker from 'vuejs-datepicker';
import placement from '@/api/placement';
import { djangoErrorsToVee } from '@/utils';

export default {
  name: 'delete-from-group-modal',
  components: {
    Datepicker,
  },
  data() {
    return {
      submitted: false,
      lang: is,
      errorList: [],
      groupPerson: {},
      title: 'Eyða þátttakanda úr hóp',
      attendanceBlockers: [],
      form: {
        full: false,
        date: null,
      },
      attendanceSummary: {
        data: null,
        loading: false,
        error: false,
      },
    };
  },
  computed: {
    disabledDates() {
      if (this.attendanceSummary.data?.dateMax) {
        return {
          to: moment(this.attendanceSummary.data.dateMax).add(1, 'days').toDate(),
        };
      }
      return {};
    },
  },
  methods: {
    async validateBeforeSubmit() {
      try {
        this.submitted = true;
        this.errorList = [];
        await this.$validator.validateAll().then(response => {
          if (!response) {
            throw 'FormValidationError'; // eslint-disable-line no-throw-literal
          }
        });
        // Engar villur í formi.
        try {
          // this.loading = true;
          await placement.placementGroupPersondDelete(
            this.groupPerson.id,
            this.form.full,
            this.form.full ? null : moment(this.form.date).format('YYYY-MM-DD'),
          );
          this.displaySuccess('Þátttakanda hefur verið eytt úr hóp');
          this.$emit('successful', {});
          this.$refs.deleteFromGroupModal.hide();
        } catch (e) {
          // Aðgerð mistókst.
          this.failed = true;
          this.$log.debug(e);
          this.loading = false;
          if (e.response && e.response.status === 409) {
            this.attendanceBlockers = e.response.data.blockers || [];
          } else if (e.response && e.response.status === 400) {
            djangoErrorsToVee(e.response.data, this.errors, this.errorList);
          } else {
            this.displayError(`Óvænt villa (${e.response ? e.response.status : -1}) kom upp. Vinsamlegast reyndu aftur.`);
          }
        }
      } catch (e) {
        // Villur í formi.
        this.$log.debug(e);
        this.loading = false;
      }
    },
    async loadAttendanceSummary() {
      this.attendanceSummary.loading = true;
      this.attendanceSummary.data = null;
      this.attendanceSummary.error = false;

      try {
        const response = await this.$placementApi.placementGroupPersonAttendanceSummary({
          uuid: this.groupPerson.id,
        });

        this.attendanceSummary.data = response.data;
      } catch (e) {
        this.displayError(`Óvænt villa kom upp. Vinsamlegast reyndu aftur.`);
        this.attendanceSummary.error = true;
      } finally {
        this.attendanceSummary.loading = false;
      }
    },
    show(groupPerson) {
      this.groupPerson = groupPerson;
      this.form.full = false;
      this.form.date = null;
      this.submitted = false;
      this.attendanceBlockers = [];
      this.$refs.deleteFromGroupModal.show();
      this.loadAttendanceSummary();
    },
    ...mapActions({
      displayError: 'displayError',
      displaySuccess: 'displaySuccess',
    }),
  },
};
</script>
